import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { createContext, use, type ComponentProps, type ReactNode } from 'react';
import type { Components } from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';
import type { Citation } from '../api/schemas';
import { fadeInSx } from './motion';

type CiteSettings = {
  byNumber: Map<number, Citation>;
  isLive: boolean;
  /** On a report page: citations read "p.3" and move that report's viewer instead of opening a link. */
  onPage?: ((page: number) => void) | undefined;
};

export const CiteContext = createContext<CiteSettings>({ byNumber: new Map(), isLive: false });

export const citeSx = {
  display: 'inline-grid',
  placeItems: 'center',
  minWidth: 16,
  height: 16,
  px: 0.5,
  mx: 0.5,
  fontSize: '0.6875rem',
  fontWeight: 600,
  lineHeight: 1,
  verticalAlign: '0.15em',
  borderRadius: 0.5,
  bgcolor: 'surface.accentSoft',
  color: 'primary.main',
  textDecoration: 'none',
  transition: 'background-color 120ms, color 120ms',
  '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
} as const;

/** Blocks fade in only while streaming; the finished answer renders still, so nothing flickers when it lands. */
function useFade() {
  return use(CiteContext).isLive ? fadeInSx : {};
}

function CiteOrLink({ href, children }: ComponentProps<'a'>) {
  const { byNumber, onPage } = use(CiteContext);
  if (href?.startsWith('#cite-')) {
    const n = Number(href.slice('#cite-'.length));
    const cite = byNumber.get(n);
    if (!cite)
      return (
        <Box component="span" sx={citeSx}>
          [{n}]
        </Box>
      );
    if (onPage) {
      return (
        <ButtonBase onClick={() => { onPage(cite.page); }} aria-label={`Open page ${cite.page}`} sx={citeSx}>
          p.{cite.page}
        </ButtonBase>
      );
    }
    return (
      <Link
        component={RouterLink}
        to={`/documents/${cite.documentId}?page=${cite.page}`}
        aria-label={`Source ${n}: ${cite.title}, page ${cite.page}`}
        sx={citeSx}
      >
        {n}
      </Link>
    );
  }
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  );
}

function Paragraph({ children }: { children?: ReactNode }) {
  return (
    <Typography
      component="p"
      variant="body2"
      sx={{ fontSize: 'inherit', lineHeight: 'inherit', my: 1, ...useFade() }}
    >
      {children}
    </Typography>
  );
}

function Heading({ children }: { children?: ReactNode }) {
  return (
    <Typography component="h4" variant="subtitle2" sx={{ mt: 2, mb: 0.75, ...useFade() }}>
      {children}
    </Typography>
  );
}

function ListItem({ children }: { children?: ReactNode }) {
  return (
    <Box component="li" sx={{ mb: 0.5, pl: 0.25, ...useFade() }}>
      {children}
    </Box>
  );
}

function Row({ children }: { children?: ReactNode }) {
  return <TableRow sx={useFade()}>{children}</TableRow>;
}

const listSx = { my: 1, pl: 2.5, '& li': { mb: 0.75 }, '& li::marker': { color: 'text.disabled' } } as const;

export const markdownComponents: Components = {
  a: ({ href, children }) => <CiteOrLink href={href}>{children}</CiteOrLink>,
  p: ({ children }) => <Paragraph>{children}</Paragraph>,
  strong: ({ children }) => (
    <Box component="strong" sx={{ fontWeight: 600, color: 'text.primary' }}>
      {children}
    </Box>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={listSx}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={listSx}>
      {children}
    </Box>
  ),
  li: ({ children }) => <ListItem>{children}</ListItem>,
  h1: ({ children }) => <Heading>{children}</Heading>,
  h2: ({ children }) => <Heading>{children}</Heading>,
  h3: ({ children }) => <Heading>{children}</Heading>,
  h4: ({ children }) => <Heading>{children}</Heading>,
  table: ({ children }) => (
    <Box sx={{ my: 1.5, overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Table size="small">{children}</Table>
    </Box>
  ),
  thead: ({ children }) => <TableHead sx={{ bgcolor: 'surface.sunken' }}>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <Row>{children}</Row>,
  th: ({ children }) => (
    <TableCell sx={{ fontWeight: 600, fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{children}</TableCell>
  ),
  td: ({ children }) => (
    <TableCell sx={{ fontSize: '0.8125rem', verticalAlign: 'top' }}>{children}</TableCell>
  ),
};
